'use client'

import { useTranslations } from 'next-intl'
import { Input, Select, SelectItem, Textarea } from '@nextui-org/react'
import { Button } from '@/shared/ui/button/button'
import { useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { readUtmAttribution } from '@/shared/lib/utm'
import { fireSellCompanySuccess } from '@/shared/lib/analytics'

const inputStyles = {
  input: 'bg-transparent',
  inputWrapper: 'bg-transparent hover:bg-default-100 rounded-md'
}

const taxSystemOptions = [
  { value: 'ОСНО', label: 'ОСНО' },
  { value: 'УСН6', label: 'УСН 6%' },
  { value: 'УСН15', label: 'УСН 15%' },
  { value: 'ЕСХН', label: 'ЕСХН' },
  { value: 'ПСН', label: 'ПСН' },
  { value: 'НПД', label: 'НПД' },
  { value: 'АУСН', label: 'АУСН' }
]

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? ''

export const CellForm = () => {
  const t = useTranslations('contact')
  const pathname = usePathname()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [cfToken, setCfToken] = useState('')
  const turnstileRef = useRef<TurnstileInstance>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setIsSuccess(false)
    setErrorMessage('')

    const formData = new FormData(e.currentTarget)

    // Тип заявки — продавец ООО
    const utm = readUtmAttribution()

    const data = {
      type: 'sell' as const,
      name: formData.get('name') as string,
      phone: formData.get('phone') as string,
      email: formData.get('email') as string,
      companyName: formData.get('companyName') as string,
      inn: formData.get('inn') as string,
      taxSystem: formData.get('taxSystem') as string,
      bankName: (formData.get('bankName') as string) || '',
      message: (formData.get('message') as string) || '',
      additionalInfo: (formData.get('additionalInfo') as string) || '',
      // Honeypot
      website: (formData.get('website') as string) || '',
      // Cloudflare Turnstile token
      cfToken,
      // UTM-атрибуция (нет ПДн)
      utm: utm ?? undefined,
      page_path: pathname
    }

    try {
      const response = await fetch('/api/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        // Конверсия фиксируется только при подтверждённом успехе API
        fireSellCompanySuccess(pathname)
        setIsSuccess(true)
        formRef.current?.reset()
        // Сбрасываем Turnstile для возможной повторной отправки
        turnstileRef.current?.reset()
        setCfToken('')
      } else {
        const json = (await response.json().catch(() => ({}))) as { message?: string }
        setErrorMessage(json.message ?? 'Ошибка отправки заявки')
        // Сбрасываем токен при ошибке — данные формы не трогаем
        turnstileRef.current?.reset()
        setCfToken('')
      }
    } catch {
      setErrorMessage('Ошибка отправки заявки')
      turnstileRef.current?.reset()
      setCfToken('')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="container py-6 md:py-12">
      <div className="mx-auto max-w-[600px]">
        <h2 className="mb-2 text-2xl md:text-3xl font-bold text-center">{t('title')}</h2>
        <p className="mb-6 md:mb-8 text-sm md:text-base text-muted-foreground text-center">
          {t('description')}
        </p>
        <form className="space-y-4" onSubmit={handleSubmit} ref={formRef}>
          {/* Honeypot — скрытое поле, нормальный пользователь не заполняет */}
          <input
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ display: 'none' }}
          />
          <Input
            name="companyName"
            label={t('form.companyName')}
            variant="bordered"
            classNames={inputStyles}
            isRequired
          />
          <Input
            name="name"
            label={t('form.name')}
            variant="bordered"
            classNames={inputStyles}
            isRequired
          />
          <Input
            name="phone"
            label={t('form.phone')}
            type="tel"
            variant="bordered"
            placeholder="+7 (999) 999-99-99"
            classNames={inputStyles}
            isRequired
          />
          <Input
            name="email"
            label={t('form.email')}
            type="email"
            variant="bordered"
            classNames={inputStyles}
            isRequired
          />
          <Input
            name="inn"
            label={t('form.inn')}
            variant="bordered"
            maxLength={10}
            minLength={10}
            placeholder="10 цифр"
            classNames={inputStyles}
            isRequired
          />
          <Select
            name="taxSystem"
            label={t('form.taxSystem')}
            variant="bordered"
            classNames={{
              trigger: 'bg-transparent hover:bg-default-100 rounded-md'
            }}
            isRequired
          >
            {taxSystemOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
          <Input
            name="bankName"
            label={t('form.bankName')}
            variant="bordered"
            classNames={inputStyles}
          />
          <Textarea
            name="message"
            label={t('form.message')}
            variant="bordered"
            minRows={3}
            classNames={inputStyles}
          />
          <Textarea
            name="additionalInfo"
            label={t('form.additionalInfo')}
            variant="bordered"
            minRows={3}
            placeholder={t('form.additionalInfoPlaceholder')}
            classNames={inputStyles}
          />
          {/* Cloudflare Turnstile — invisible/managed режим */}
          {TURNSTILE_SITE_KEY && (
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setCfToken(token)}
              onExpire={() => setCfToken('')}
              onError={() => setCfToken('')}
              options={{ theme: 'light', size: 'flexible' }}
            />
          )}
          <Button
            type="submit"
            className="w-full h-11 bg-orange-500 hover:bg-orange-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Отправка...' : t('form.submit')}
          </Button>
          {isSuccess && (
            <p className="text-center text-green-600">Заявка успешно отправлена!</p>
          )}
          {errorMessage && (
            <p className="text-center text-red-600">{errorMessage}</p>
          )}
        </form>
      </div>
    </section>
  )
}
